import Backdrop from 'components/backdrop';
import ContentBlock from 'components/content-block';
import Layout from 'components/layout';
import SEO from 'components/seo';
import {
  Button,
  Heading,
  Image,
  ListItem,
  Stack,
  Tag,
  Text,
} from 'components/ui';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import ErrorPage from 'next/error';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { REVALIDATE_TIME, SETTINGS } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/theme';
import { __DEV__ } from 'utils/helpers';
import {
  getSpecialistPaths,
  SharedData,
  sharedDataQuery,
  specialistQuery,
  SpecialistWithExpandedRefs,
} from 'utils/queries';
import { urlForImage, usePreviewSubscription } from 'utils/sanity';
import { getClient } from 'utils/sanity-server';
import instagramIcon from '../../images/instagram.svg';
import emailIcon from '../../images/mail.svg';
import phoneIcon from '../../images/phone.svg';
import { ReactComponent as PinIcon } from '../../images/pin.svg';
import { ReactComponent as ServicesIcon } from '../../images/services.svg';
import webIcon from '../../images/web.svg';

interface SpecialistPageProps {
  pageData?: SpecialistWithExpandedRefs;
  sharedData?: SharedData;
  preview?: boolean;
}

const LocationButton = styled(Button)`
  display: flex;
  align-items: center;

  svg {
    margin-right: var(--space-0);

    * {
      fill: currentColor;
    }
  }
`;

const OverflowAnchor = styled.a`
  word-break: break-word;
`;

const TagGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-0);
  text-align: center;

  @media screen and (min-width: ${breakpoints.large}) {
    gap: var(--space-2);
  }

  * {
    font-size: var(--font-size-xxx-small);
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--space-1);
  max-width: 1040px;
  margin: 0 auto;
`;

const StyledServicesIcon = styled(ServicesIcon)`
  @media screen and (min-width: ${breakpoints.medium}) {
    position: absolute;
    top: 0;
    background: none;
    transform: translateY(-50%);
  }
`;

const ContactStack = styled(Stack)`
  --icon-size: 24px;

  font-size: var(--font-size-small);

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ${ListItem} {
    &::before {
      top: 0;
    }
  }
`;

const SpecialistPage: NextPage<SpecialistPageProps> = ({
  pageData,
  sharedData,
  preview,
}) => {
  const router = useRouter();

  const { data: page } = usePreviewSubscription(specialistQuery, {
    params: { slug: pageData?.slug?.current },
    initialData: pageData,
    enabled: preview,
  });

  const { data: shared } = usePreviewSubscription(sharedDataQuery, {
    initialData: sharedData,
    enabled: preview,
  });

  if (router.isFallback) {
    return <Layout>Loading...</Layout>;
  }

  if (!page) {
    return <ErrorPage statusCode={404} />;
  }

  const specialistImage = urlForImage(page.image).width(1024).url();
  const servicesImage = urlForImage(page.servicesImage).width(1024).url();

  return (
    <Layout preview={preview} spaceLinks={shared.spaceInfoPaths}>
      <SEO title={page.name} description={page.bio} image={page.image} />
      <ContentBlock flourishTopLeft='small' flourishBottomRight='small'>
        {specialistImage && (
          <Image
            src={specialistImage}
            width={1024}
            height={1024}
            alt={page.image?.alt || ''}
            objectFit='cover'
          />
        )}
        <Stack space={{ initial: 2, large: 4, xLarge: 6 }}>
          <Stack space={2}>
            <Heading level='h2' as='h1'>
              {page.name}
            </Heading>
            <Heading level='h5' as='h2' variant='sans-serif'>
              {page.specialistType?.name}
            </Heading>
            <Link href={SETTINGS.routes.locations.slug}>
              <LocationButton>
                <PinIcon />
                {page.location?.city}, {page.location?.state}
              </LocationButton>
            </Link>
          </Stack>
          <Text fontWeight='light' fontSize='xSmall'>
            {page.bio}
          </Text>
        </Stack>
      </ContentBlock>
      <ContentBlock backgroundColor='grey100'>
        <Stack space={{ initial: 2, large: 4, xLarge: 6 }}>
          <StyledServicesIcon />
          <Heading level='h2'>Services</Heading>
          <TagGrid>
            {page.services?.map((service) => (
              <Tag backgroundColor='grey' key={service._key}>
                {service.service?.name} {service.price && `- $${service.price}`}
              </Tag>
            ))}
          </TagGrid>
        </Stack>
        {servicesImage && (
          <Image
            src={servicesImage}
            width={1024}
            height={1024}
            alt={page.servicesImage?.alt || ''}
            objectFit='cover'
          />
        )}
      </ContentBlock>
      {page.images && (
        <Backdrop>
          <ImageGrid>
            {page.images.map((image) => {
              const imageUrl = urlForImage(image).width(240).height(240).url();
              return (
                <Image
                  src={imageUrl || ''}
                  key={image._key}
                  width={240}
                  height={240}
                  objectFit='cover'
                  alt={image.alt || ''}
                />
              );
            })}
          </ImageGrid>
        </Backdrop>
      )}
      {page.contact && (
        <ContentBlock backgroundColor='grey100'>
          <Heading level='h2'>Contact</Heading>
          <ContactStack space={2}>
            {page.contact.email && (
              <ListItem image={emailIcon} as='span'>
                <OverflowAnchor href={`mailto:${page.contact.email}`}>
                  {page.contact.email}
                </OverflowAnchor>
              </ListItem>
            )}
            {page.contact.phone && (
              <ListItem image={phoneIcon} as='span'>
                <OverflowAnchor href={`tel:${page.contact.phone}`}>
                  {page.contact.phone}
                </OverflowAnchor>
              </ListItem>
            )}
            {page.contact.website && (
              <ListItem image={webIcon} as='span'>
                <OverflowAnchor href={page.contact.website}>
                  {page.contact.website}
                </OverflowAnchor>
              </ListItem>
            )}
            {page.contact.instagram && (
              <ListItem image={instagramIcon} as='span'>
                <OverflowAnchor
                  target='_blank'
                  href={`http://instagram.com/${page.contact.instagram}`}
                >
                  {page.contact.instagram}
                </OverflowAnchor>
              </ListItem>
            )}
            {/* Button link to bookings system */}
            {page.contact.bookings && (
              <Link href={page.contact.bookings} passHref>
                <Button variant='primary' as='a' target={'_blank'}>
                  Make a Booking
                </Button>
              </Link>
            )}
          </ContactStack>
        </ContentBlock>
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<SpecialistPageProps> = async ({
  params,
  preview = __DEV__,
}) => {
  const pageData = await getClient(preview).fetch<SpecialistWithExpandedRefs>(
    specialistQuery,
    { slug: params?.slug }
  );
  const sharedData = await getClient(preview).fetch<SharedData>(
    sharedDataQuery
  );

  return {
    props: {
      preview,
      pageData,
      sharedData,
    },
    revalidate: REVALIDATE_TIME,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getSpecialistPaths(false);

  return {
    paths: paths.map(({ slug }) => ({ params: { slug } })),
    fallback: true,
  };
};

export default SpecialistPage;
